import React, { useContext } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import logo from '../logo.png';
import NotificationBell from './NotificationBell';

const pages = ['Today', 'Statistics', 'Calendar', 'Relationship'];

function Header() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [imageTimestamp, setImageTimestamp] = React.useState(Date.now());
  const { user, authTokens, user_name, logoutUser, profile, backendUrl, isAuthenticated } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Update timestamp when profile picture changes
  React.useEffect(() => {
    if (profile?.profile_picture) {
      setImageTimestamp(Date.now());
    }
  }, [profile?.profile_picture]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const goToPage = (page) => {
    const path = '/' + page.toLowerCase();
    navigate(path, { replace: true });
    setMobileOpen(false);
  };

  const handleUserMenuClick = (setting) => {
    handleCloseUserMenu();
    if (setting === 'Profile') {
      navigate('/Profile', { replace: true });
    } else if (setting === 'Feedback') {
      navigate('/feedback', { replace: true });
    } else if (setting === 'Logout') {
      logoutUser();
    }
  };

  if (!isAuthenticated()) {
    return <></>;
  }

  if (window.location.pathname === '/login') return null;

  const isPageActive = (page) => {
    return location.pathname === `/${page.toLowerCase()}`;
  };

  // Mobile drawer
  const drawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        backgroundColor: '#F9F7F4',
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #E8E8E8'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img
            src={logo}
            alt="Couplendar"
            style={{
              width: 32,
              height: 32,
              objectFit: 'contain'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#7B6B8D',
            }}
          >
            Couplendar
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ mt: 2 }}>
        {pages.map((page) => (
          <ListItem key={page} disablePadding>
            <ListItemButton
              onClick={() => goToPage(page)}
              sx={{
                mx: 1,
                borderRadius: 2,
                mb: 0.5,
                backgroundColor: isPageActive(page) ? alpha('#7B6B8D', 0.1) : 'transparent',
                '&:hover': {
                  backgroundColor: alpha('#7B6B8D', 0.15),
                }
              }}
            >
              <ListItemText
                primary={page}
                primaryTypographyProps={{
                  fontWeight: isPageActive(page) ? 600 : 400,
                  color: isPageActive(page) ? '#7B6B8D' : '#333',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #E8E8E8',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 } }}>
            {/* Mobile menu icon */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, color: '#333' }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexGrow: { xs: 1, md: 0 },
                cursor: 'pointer'
              }}
              onClick={() => navigate(isAuthenticated() ? '/today' : '/')}
            >
              <img
                src={logo}
                alt="Couplendar"
                style={{
                  width: 36,
                  height: 36,
                  objectFit: 'contain'
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#7B6B8D',
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}
              >
                Couplendar
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              gap: 1
            }}>
              {pages.map((page) => (
                <Button
                  key={page}
                  onClick={() => goToPage(page)}
                  sx={{
                    px: 3,
                    py: 1,
                    color: isPageActive(page) ? '#7B6B8D' : '#666',
                    fontWeight: isPageActive(page) ? 600 : 500,
                    borderRadius: 2,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: alpha('#7B6B8D', 0.08),
                      color: '#7B6B8D',
                    },
                    '&::after': isPageActive(page) ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: 3,
                      backgroundColor: '#7B6B8D',
                      borderRadius: '3px 3px 0 0',
                    } : {}
                  }}
                >
                  {page}
                </Button>
              ))}
            </Box>

            {/* Notifications */}
            <Box sx={{ flexGrow: 0, mr: 1 }}>
              <NotificationBell />
            </Box>

            {/* User Avatar */}
            <Box sx={{ flexGrow: 0 }}>
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{
                  p: 0,
                  border: '2px solid',
                  borderColor: alpha('#7B6B8D', 0.2),
                  '&:hover': {
                    borderColor: '#7B6B8D',
                  }
                }}
              >
                <Avatar
                  alt={profile?.first_name || user_name}
                  src={profile?.profile_picture ? `${backendUrl}${profile.profile_picture}?t=${imageTimestamp}` : null}
                  sx={{
                    width: { xs: 36, md: 40 },
                    height: { xs: 36, md: 40 },
                    backgroundColor: '#7B6B8D',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {profile?.first_name?.charAt(0)}{profile?.last_name?.charAt(0)}
                </Avatar>
              </IconButton>
              <Menu
                sx={{
                  mt: '45px',
                  '& .MuiPaper-root': {
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    minWidth: 160
                  }
                }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem
                  onClick={() => handleUserMenuClick('Profile')}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: alpha('#7B6B8D', 0.08),
                    }
                  }}
                >
                  <Typography textAlign="center">Profile</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => handleUserMenuClick('Feedback')}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: alpha('#7B6B8D', 0.08),
                    }
                  }}
                >
                  <Typography textAlign="center">Feedback</Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => handleUserMenuClick('Logout')}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: alpha('#C4856A', 0.08),
                    }
                  }}
                >
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            border: 'none'
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Header;
